import {
  Component,
  HostBinding,
  Inject,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
  Data,
} from "@angular/router";
import { select, Store } from "@ngrx/store";
import { LoadingBarService } from "@ngx-loading-bar/core";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "./base.component";
import {
  ApiStateInterface,
  selectApiState,
  setAPIStatus,
} from "./store/apiState.interface";
import { AppStateInterface } from "./store/appState.interface";
import { ToastrService } from "ngx-toastr";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { languageData } from "./shared/data/languages";
import { NavigatorService } from "./shared/services/navigator/navigator.service";
import { filter, map, mergeMap, tap } from "rxjs/operators";
import { MetaService } from "./shared/services/meta/meta.service";
import {
  catchForbiddenError,
  catchServerError,
} from "./store/user/user.actions";
import { RoleEnum } from "./shared/models/user/role.interface";
import {
  invokeLPInfo,
  resultLPInfo,
} from "./store/locationPartner/actions/get-locations.actions";
import { AuthorityService } from "./shared/services/authority.service";
import { JwtService } from "./shared/services/jwt.service";
import {
  getAllFeatures,
  invokeAllFeatures,
} from "./store/features/features.actions";
import { invokeFranchiseInfo } from "./store/franchise/franchise.action";
import { ClarityService } from "./shared/services/clarity.service";
import { UtilFunctions } from "./utils/utilFunctions";
import { Account } from "./shared/models/user/account";
import { accountSelector } from "./store/auth/auth.selectors";
import { Constants } from "./shared/data/constants";
import { PosthogService } from "./shared/services/posthog.service";

declare let gtag: Function;
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent extends BaseComponent implements OnInit {
  @HostBinding("@.disabled")
  public animationsDisabled = false;

  ApiState$: Observable<ApiStateInterface>;

  errorCount: number = 0;
  locale: string;
  roleName: string;
  clarityLoaded: boolean = false;

  account$: Observable<Account | null>;
  prepareRoute(outlet: RouterOutlet) {
    return (
      outlet &&
      outlet.activatedRouteData &&
      outlet.activatedRouteData["animation"]
    );
  }

  toggleAnimations() {
    this.animationsDisabled = !this.animationsDisabled;
  }
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private loader: LoadingBarService,
    private store: Store<AppStateInterface>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private navigatorService: NavigatorService,
    private metaService: MetaService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private jwtService: JwtService,
    private clarityService: ClarityService,
    private posthogService: PosthogService
  ) {
    super(store);
    this.ApiState$ = this.store
      .pipe(select(selectApiState))
      .pipe(takeUntil(this.ngDestroyed$));
    this.account$ = this.store
      .pipe(select(accountSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    translate.addLangs(["en", "fr"]);
    const storedLang: string =
      localStorage.getItem("lang") ?? languageData[1].key;
    if (storedLang) {
      translate.use(storedLang);
    } else {
      translate.setDefaultLang("fr");
      translate.use("fr");
    }
    this.roleName = this.jwtService.getRoleName(this.jwtService.getToken());
    /** START : Code to Track Page View using gtag.js */
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.clarityService.loadClarity();
        this.setTagToClarity(this.roleName);
        this.account$.subscribe((account) => {
          if (account?.role && account.user) {
            this.setTagToClarity(account?.role);
          }
        });
        if (!this.isIncludedRoute(event.urlAfterRedirects)) {
          return;
        }
        this.setupGoogleAnalytics();
        gtag("event", "page_view", {
          page_path: event.urlAfterRedirects,
        });
      });
    /** END : Code to Track Page View  using gtag.js */
  }
  private isIncludedRoute(route: string): boolean {
    // Add your included routes here
    const includedRoutes = "/dashboard/advertiser/";
    return route.includes(includedRoutes);
  }
  ngOnInit(): void {
    this.reactivateOrCutCapturingWithPosthog(this.roleName);
    this.account$.subscribe((account) => {
      if (account?.role && account.user) {
        this.reactivateOrCutCapturingWithPosthog(account?.role);
      }
    });
    this.navigatorService
      .getIpAddress()
      .subscribe((value) => localStorage.setItem("Ip-Address", value.ip));

    this.ApiState$.subscribe((data) => {
      if (data.apiStatus === "error") {
        const isClickUpError =
          data.apiResponseMessage.url &&
          data.apiResponseMessage.url.includes(Constants.CLICKUP);

        if (isClickUpError) {
          this.store.dispatch(
            setAPIStatus({
              apiStatus: { apiResponseMessage: "", apiStatus: "" },
            })
          );
          return;
        }

        if (data.apiResponseMessage.status == 401) {
          if (this.errorCount == 0) {
            this.errorCount++;
            this.toastr.error(
              this.translate.instant("response.error.REVOKED_ACCOUNT"),
              "UNAUTHORIZED"
            );
            localStorage.removeItem("token");
            this.router.navigate(["/auth/login"]);
            setTimeout(() => {
              this.errorCount = 0;
            }, 1000);
          }
        } else if (
          data.apiResponseMessage.statusText == "Unknown Error" ||
          data.apiResponseMessage.status == 500
        ) {
          this.store.dispatch(catchServerError({ serverError: true }));
          if (this.errorCount == 0) {
            this.errorCount++;
            this.toastr.error(
              this.translate.instant("response.error.undefined"),
              this.translate.instant("response.errorTitle")
            );
            setTimeout(() => {
              this.errorCount = 0;
            }, 1000);
          }
        } else if (
          data.apiResponseMessage.status == 403 ||
          data.apiResponseMessage.httpCode == 403
        ) {
          this.store.dispatch(catchForbiddenError({ forbiddenError: true }));
          if (this.errorCount == 0) {
            this.errorCount++;
            this.toastr.error(
              this.translate.instant("response.error.FORBIDDEN_RESPONSE"),
              this.translate.instant("response.errorTitle")
            );
            setTimeout(() => {
              this.errorCount = 0;
            }, 1000);
          }
        } else {
          this.store.dispatch(catchServerError({ serverError: true }));
          const errorMessage =
            data.apiResponseMessage.error?.message ||
            data.apiResponseMessage.message;
          this.toastr.error(
            this.translate.instant("response.error." + errorMessage),
            this.translate.instant("response.errorTitle")
          );
        }
        this.store.dispatch(
          setAPIStatus({ apiStatus: { apiResponseMessage: "", apiStatus: "" } })
        );
      }

      if (data.apiStatus === "success") {
        this.store.dispatch(
          setAPIStatus({ apiStatus: { apiResponseMessage: "", apiStatus: "" } })
        );
      }
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === "primary"),
        mergeMap((route) => route.data),
        tap(({ title, description }: Data) => {
          this.metaService.updateTitle(title);
          this.metaService.updateDescription(description);
        })
      )
      .subscribe();

    this.locale = this.translate.currentLang;
    // don't forget to unsubscribe!
    this.translate.onLangChange.subscribe(
      (langChangeEvent: LangChangeEvent) => {
        this.locale = langChangeEvent.lang;
      }
    );

    this.store.dispatch(resultLPInfo({ lpInfo: null }));
    if (this.roleName && this.roleName === RoleEnum.ROLE_LOCATION) {
      this.store.dispatch(invokeLPInfo());
    }

    if (this.roleName && this.roleName === RoleEnum.ROLE_FRANCHISE) {
      this.store.dispatch(invokeFranchiseInfo());
    }

    if (this.roleName && this.roleName !== RoleEnum.ROLE_ADMIN) {
      this.store.dispatch(getAllFeatures({ features: null }));
      this.store.dispatch(
        invokeAllFeatures({ email: this.jwtService.getEmail() })
      );
    }

    // Initialize Authority Service
    const authorities = this.jwtService.getPermissions(
      this.jwtService.getToken()
    );
    AuthorityService.setAuthorities(authorities ?? []);
  }

  /** Add Google Analytics Script Dynamically */
  setupGoogleAnalytics(): void {
    const gtagScript: HTMLScriptElement = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=G-XZXPEB2VR5";
    document.head.prepend(gtagScript);
    /** Disable automatic page view hit to fix duplicate page view count */
    gtag("config", "G-XZXPEB2VR5", { send_page_view: false });
  }

  setTagToClarity(roleName: string) {
    if (
      roleName === RoleEnum.ROLE_LOCATION &&
      UtilFunctions.isFranchised(this.jwtService)
    ) {
      this.clarityService.setUserType("ROLE_FRANCHISED");
    } else {
      this.clarityService.setUserType(roleName);
    }
  }

  reactivateOrCutCapturingWithPosthog(roleName: string) {
    if (roleName=== RoleEnum.ROLE_ADMIN) {
      this.posthogService.disableTracking()
      return;
    }
    this.posthogService.initTracking();
    this.posthogService.enableTracking()
  }
}
